var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { WcSdk } from '@cityofzion/wallet-connect-sdk-core';
import { BaseWalletAdapter, WalletAccountError, WalletConnectionError, WalletDisconnectedError, WalletDisconnectionError, WalletError, WalletNotConnectedError, } from '@rentfuse-labs/neo-wallet-adapter-base';
import QRCodeModal from '@walletconnect/qrcode-modal';
export class WalletConnectWalletAdapter extends BaseWalletAdapter {
    constructor(config, neonWallet) {
        super();
        this._walletConnectChains = ['neo3:mainnet', 'neo3:testnet', 'neo3:private'];
        this._address = null;
        this._connecting = false;
        this._options = config.options;
        this._logger = config.logger;
        this._relayProvider = config.relayProvider;
        this._neonWallet = neonWallet ? neonWallet : false;
    }
    get address() {
        return this._address;
    }
    get ready() {
        return typeof window !== 'undefined';
    }
    get connecting() {
        return this._connecting;
    }
    get connected() {
        return !!this._address;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.connected || this.connecting)
                    return;
                this._connecting = true;
                let walletConnectInstance;
                try {
                    // Create walletconnect coz instance
                    walletConnectInstance = new WcSdk();
                    // Initialize it
                    yield walletConnectInstance.initClient(this._logger, this._relayProvider);
                    let neonWalletConnectWindow;
                    // Subscribe to wc events
                    walletConnectInstance.subscribeToEvents({
                        onProposal: (uri) => {
                            if (this._neonWallet) {
                                neonWalletConnectWindow = window === null || window === void 0 ? void 0 : window.open(`https://neon.coz.io/connect?uri=${uri}`, '_blank');
                                neonWalletConnectWindow === null || neonWalletConnectWindow === void 0 ? void 0 : neonWalletConnectWindow.focus();
                            }
                            else {
                                // show the QRCode, you can use @walletconnect/qrcode-modal to do so, but any QRCode presentation is fine
                                QRCodeModal.open(uri, () => {
                                    // Eheh just show that!
                                });
                            }
                        },
                        onDeleted: () => {
                            // here is where you describe a logout callback
                            this._disconnected();
                        },
                    });
                    // Load any existing connection, it should be called after the initialization, to reestablish connections made previously
                    yield walletConnectInstance.loadSession();
                    // If the session has not been loaded try to load it
                    if (!walletConnectInstance.session) {
                        // If we're here we need to connect
                        yield walletConnectInstance.connect(this._options);
                        // the promise will be resolved after the connection is accepted or refused, you can close the QRCode modal here
                        if (this._neonWallet && neonWalletConnectWindow) {
                            neonWalletConnectWindow.close();
                        }
                        else {
                            QRCodeModal.close();
                        }
                    }
                }
                catch (error) {
                    if (error instanceof WalletError)
                        throw error;
                    throw new WalletConnectionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                if (!walletConnectInstance.session)
                    throw new WalletAccountError();
                this._address = walletConnectInstance.accountAddress;
                this._walletConnectInstance = walletConnectInstance;
                this.emit('connect');
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
            finally {
                this._connecting = false;
            }
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            const walletConnectInstance = this._walletConnectInstance;
            if (walletConnectInstance && walletConnectInstance.session) {
                try {
                    yield walletConnectInstance.disconnect();
                }
                catch (error) {
                    this.emit('error', new WalletDisconnectionError(error === null || error === void 0 ? void 0 : error.message, error));
                }
                finally {
                    this._address = null;
                    this._walletConnectInstance = undefined;
                }
            }
            this.emit('disconnect');
        });
    }
    invokeRead(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletConnectInstance = this._walletConnectInstance;
            if (!walletConnectInstance || !walletConnectInstance.session)
                throw new WalletNotConnectedError();
            try {
                const response = yield walletConnectInstance.testInvoke({
                    scriptHash: request.scriptHash,
                    operation: request.operation,
                    args: request.args,
                    abortOnFail: request.abortOnFail,
                }, request.signers);
                return this._responseToReadResult(response);
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    invokeReadMulti(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletConnectInstance = this._walletConnectInstance;
            if (!walletConnectInstance || !walletConnectInstance.session)
                throw new WalletNotConnectedError();
            try {
                const response = yield walletConnectInstance.testInvoke(request.invocations, request.signers);
                return this._responseToReadResult(response);
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    invoke(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletConnectInstance = this._walletConnectInstance;
            if (!walletConnectInstance || !walletConnectInstance.session)
                throw new WalletNotConnectedError();
            try {
                const response = yield walletConnectInstance.invokeFunction({
                    scriptHash: request.scriptHash,
                    operation: request.operation,
                    args: request.args,
                    abortOnFail: request.abortOnFail,
                }, request.signers);
                return this._responseToWriteResult(response);
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    invokeMulti(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletConnectInstance = this._walletConnectInstance;
            if (!walletConnectInstance || !walletConnectInstance.session)
                throw new WalletNotConnectedError();
            try {
                const response = yield walletConnectInstance.invokeFunction(request.invocations, request.signers);
                return this._responseToWriteResult(response);
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    getNetworks() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const walletConnectInstance = this._walletConnectInstance;
                if (!walletConnectInstance || !walletConnectInstance.session)
                    throw new WalletNotConnectedError();
                return this._responseToGetNetworksResult(walletConnectInstance.chainId ? walletConnectInstance.chainId : '');
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    signMessage(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletConnectInstance = this._walletConnectInstance;
            if (!walletConnectInstance || !walletConnectInstance.session)
                throw new WalletNotConnectedError();
            try {
                const response = yield walletConnectInstance.signMessage(request.message);
                return this._responseToSignMessageResult(response);
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    _responseToReadResult(response) {
        var _a, _b;
        // If the state is halt it means that everything went well
        if (response.result.state === 'HALT') {
            return {
                status: 'success',
                data: Object.assign({}, response.result),
            };
        }
        // Otherwise an error occurred and so return it
        return {
            status: 'error',
            message: (_a = response.result.error) === null || _a === void 0 ? void 0 : _a.message,
            code: (_b = response.result.error) === null || _b === void 0 ? void 0 : _b.code,
        };
    }
    _responseToWriteResult(response) {
        var _a, _b;
        // If the state is halt it means that everything went well
        if (response.result.state === 'HALT') {
            return {
                status: 'success',
                data: Object.assign({}, response.result),
            };
        }
        // Otherwise an error occurred and so return it
        return {
            status: 'error',
            message: (_a = response.result.error) === null || _a === void 0 ? void 0 : _a.message,
            code: (_b = response.result.error) === null || _b === void 0 ? void 0 : _b.code,
        };
    }
    _responseToGetNetworksResult(response) {
        return {
            status: 'success',
            data: {
                networks: this._walletConnectChains,
                defaultNetwork: response,
            },
        };
    }
    _responseToSignMessageResult(response) {
        return {
            status: 'success',
            data: {
                publicKey: response.result.publicKey,
                data: response.result.data,
                salt: response.result.salt,
                message: response.result.messageHex,
            },
        };
    }
    _disconnected() {
        const walletConnectInstance = this._walletConnectInstance;
        if (walletConnectInstance) {
            this._address = null;
            this._walletConnectInstance = undefined;
            this.emit('error', new WalletDisconnectedError());
            this.emit('disconnect');
        }
    }
}
//# sourceMappingURL=adapter.js.map